class RecentlyViewed extends HTMLElement {
  constructor() {
    super()
    this.carouselWrapper = document.querySelector('.recently-viewed__wrapper')
    this.carousel = document.querySelector('.recently-viewed__carousel')
  }

  static get cookieKey() { return 'recentlyViewedProducts' }

  get prevButton() {
    return this.cachedPrevButton ??= this.querySelector("[data-behavior='slide-arrow-prev']")
  }
  
  get nextButton() {
    return this.cachedNextButton ??= this.querySelector("[data-behavior='slide-arrow-next']")
  }

  getCookie() {
    return `; ${document.cookie}`.split(`; ${this.constructor.cookieKey}=`)[1]?.split(";")[0]
  }

  setCookie(value) {
    document.cookie = `${this.constructor.cookieKey}=${value}; Max-Age=2592000; path=/; secure; samesite=lax; partitioned;"`
  }

  removeComponent() {
    this.carouselWrapper.remove()
  }

  fetchRecentlyViewedProducts() {
    return JSON.parse(this.getCookie() || "[]")
  }

  async fetchProductData(productHandles) {
    const products = Promise.all(productHandles.map(handle => {
      return fetch(`/products/${handle}?view=recently-viewed`)
              .then(response => {
                if (response.ok) {
                  return response.text()
                }
              })
              .catch(_err => null)
    }))
    
    return (await products).filter(Boolean)
  }

  setContent(content) {
    this.carousel.innerHTML = content
  }

  addToHistory(handle) {
    const recentlyViewedProducts = this.fetchRecentlyViewedProducts()
    
    // if array includes product id already.
    // remove from array and then add it to the front of the array
    if (recentlyViewedProducts.includes(handle)) {
      const productPosition = recentlyViewedProducts.indexOf(handle)

      if (productPosition > -1) {
        recentlyViewedProducts.splice(productPosition, 1)
      }
    }

    recentlyViewedProducts.unshift(handle)

    // limit to 12 items always use the first 12 as we are adding to the front
    const limitedArray = recentlyViewedProducts.splice(0, recentlyViewedProducts.length < 12 ? recentlyViewedProducts.length : 12)

    this.setCookie(JSON.stringify(limitedArray))
  }

  enableArrows() {
    const slide = this.querySelector(".product-grid__item")

    this.prevButton.parentElement.classList.remove("hidden")
    this.nextButton.parentElement.classList.remove("hidden")
  
    this.nextButton.addEventListener("click", (event) => {
      this.carousel.scrollLeft += slide.clientWidth
    })
  
    this.prevButton.addEventListener("click", () => {
      this.carousel.scrollLeft -= slide.clientWidth
    })
  }

  enableCarouselObserver() {
    // we use the intersection observer which is attached to the carousel
    // to enable/disable arrows
    this.carouselObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target.matches(":first-child")) {
            this.prevButton.classList.toggle("product-carousel__arrow-disabled", entry.isIntersecting)
          } else if (entry.target.matches(":last-child")) {
            this.nextButton.classList.toggle("product-carousel__arrow-disabled", entry.isIntersecting)
          }
        })
      },
      {
        root: this.carousel,
        rootMargin: '0px',
        threshold: 1.0,
      },
    )


    this.carousel.querySelectorAll(":scope > :first-child, :scope > :last-child").forEach((element) => {
      this.carouselObserver.observe(element)
    })
  }

  async handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return
    const recentlyViewedProducts = this.fetchRecentlyViewedProducts()

    // if there is a cookie present then fetch data
    // otherwise remove the component
    if (recentlyViewedProducts.length > 0) {
      // we dont want to show the same product in the component as the one you are
      // currently viewing, so remove it from the array if so
      if (recentlyViewedProducts.includes(this.dataset.currentHandle)) {
        const productPosition = recentlyViewedProducts.indexOf(this.dataset.currentHandle)

        if (productPosition > -1) {
          recentlyViewedProducts.splice(productPosition, 1)
        }
      }

      const productData = await this.fetchProductData(recentlyViewedProducts)

      if (productData.length > 0) {
        this.setContent(productData.join(''))
        // enable carousel arrows if we have more than 4 items
        if (productData.length > 4) {
          // only enable if its at dekstop
          if (window.matchMedia("(min-width: 1024px)").matches) {
            this.enableArrows()
            this.enableCarouselObserver()
          }
        }
        this.observer.disconnect()
      } else {
        this.observer.disconnect()
        this.removeComponent()
      }
    } else {
      // we need to remove the component completely
      this.observer.disconnect()
      this.removeComponent()
    }
  }

  async connectedCallback() {
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this))
    this.observer.observe(this)

    if (this.dataset.currentHandle) {
      this.addToHistory(this.dataset.currentHandle)
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }

    if (this.carouselObserver) {
      this.carouselObserver.disconnect()
      this.carouselObserver = null
    }
  }
}

customElements.define('recently-viewed', RecentlyViewed)